import { combine, createEffect, createEvent, createStore, sample } from "effector";
import { reset } from "patronum";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { RoadmapsApi } from "@features/shared/api/roadmaps.api";
import {
    IRoadmap,
    IRoadmapCourse,
    IRoadmapsConfig,
    IRoadmapsResponse
} from "@features/shared/entities/roadmaps";
import { UserEnrollmentStatus } from "@features/shared/entities/status";
import { IUserAuthContract } from "@features/student/auth/model";
import { IAmplitudeFlagsContract, RoadmapOnboardingExperimentArm } from "@features/amplitude-flags/model";
import { IMetadataContract } from "@features/student/metadata/model";
import { ILessonsContract } from "@features/lessons/model";
import { resolveFallbackRoadmapId } from "@components/organisms/mainPage/roadmapConfig";
import { contentLocaleChanged } from "@features/shared/locale";
import { AnalyticsService } from "@features/shared/analytics";


export const selectedRoadmapChanged = createEvent<number>();
export const roadmapSelectorOpenChanged = createEvent<boolean>();

export const $roadmaps = createStore<IRoadmap[]>([], { skipVoid: false });
export const $isRoadmapSelectorOpen = createStore(false);
export const $roadmapsLoading = createStore(false);
export const $selectedRoadmapId = createStore<number>(0);
export const $selectedRoadmap = combine($roadmaps, $selectedRoadmapId, (roadmaps, id) =>
    roadmaps.find((r) => r.id === id) ?? roadmaps[0] ?? null
);
export const $activeRoadmapCourse = createStore<IRoadmapCourse | null>(null);
export const $isRoadmapHeroVisible = createStore<boolean>(false);
// GET /roadmaps/config: the roadmap sequence + the student's funnel
// recommendation. null = config not loaded / failed -> every consumer falls
// back to the static order and the learning roadmap.
export const $roadmapsConfig = createStore<IRoadmapsConfig | null>(null);
// The recommended roadmap key after the experiment arm is applied (funnel
// recommendation or the sequence default); null without a config.
const $recommendedRoadmapKey = createStore<number | null>(null);
// The recommended roadmap the UI actually uses — pinned first in the selector,
// badged "Recommended for you" and preselected on product onboarding. The key
// is validated against the fetched roadmaps (a drafted/deleted roadmap cannot
// be recommended) and falls back to the learning roadmap; null only while the
// roadmap list is empty.
export const $recommendedRoadmapId = combine($roadmaps, $recommendedRoadmapKey, (roadmaps, key) => {
    if (roadmaps.length === 0) return null;
    if (key !== null && roadmaps.some((r) => r.id === key)) return key;
    return resolveFallbackRoadmapId(roadmaps);
});

export const getRoadmapsFx = createEffect(async (): Promise<IRoadmapsResponse> => {
    return await RoadmapsApi.getRoadmaps();
});

export const getRoadmapsConfigFx = createEffect(async (): Promise<IRoadmapsConfig> => {
    return await RoadmapsApi.getRoadmapsConfig();
});

// Funnel identity as Amplitude user properties, so analysts can segment any
// chart by the purchase funnel without joining backend data.
const setFunnelUserPropsFx = createEffect((config: IRoadmapsConfig) => {
    AnalyticsService.setUserProps({
        purchase_onboarding_id: config.onboardingId,
        purchase_funnel_variant: config.variant
    });
});

sample({
    clock: getRoadmapsFx.pending,
    target: $roadmapsLoading
});


// Which roadmap is recommended:
// - recommendation with empty experimentKey -> it (permanent per-funnel config)
// - recommendation with experimentKey       -> it only on the treatment arm
// - otherwise                               -> the sequence's default recommendation
// - config missing                          -> null (static fallback end-to-end)
export const resolveRecommendedRoadmapKey = (
    config: IRoadmapsConfig | null,
    arm: RoadmapOnboardingExperimentArm | null
): number | null => {
    if (!config) return null;
    const recommendation = config.recommendation;
    if (recommendation && (!recommendation.experimentKey || arm === "treatment")) {
        return recommendation.roadmapKey;
    }
    return config.defaultRecommendedRoadmapKey || null;
};

export const resolveSelectedId = ({
    roadmaps,
    metadata,
    recommendedId
}: {
    roadmaps: IRoadmap[];
    metadata: Record<string, string>;
    recommendedId: number | null;
}): number => {
    if (roadmaps.length === 0) return 0;

    // 1. Explicit user choice saved in metadata
    const saved = parseInt(metadata.selected_roadmap, 10);
    if (!Number.isNaN(saved) && saved > 0 && roadmaps.some((r) => r.id === saved)) {
        return saved;
    }

    // 2. The recommended roadmap ($recommendedRoadmapId: funnel recommendation,
    // sequence default or the learning-roadmap fallback)
    if (recommendedId !== null && roadmaps.some((r) => r.id === recommendedId)) {
        return recommendedId;
    }

    // 3. Last resort: min id
    const sorted = [...roadmaps].sort((a, b) => a.id - b.id);
    return sorted[0].id;
};


export const resolveActiveCourse = (roadmap: IRoadmap | null): IRoadmapCourse | null => {
    if (!roadmap) return null;

    const allCourses = roadmap.steps.flatMap((s) => s.courses);

    if (allCourses.length === 0) return null;

    // Prefer courses in progress (most recently started)
    const inProgress = allCourses
        .filter((c) => c.enrollmentStatus === UserEnrollmentStatus.Progress)
        .sort((a, b) => {
            if (!a.startedAt && !b.startedAt) return 0;
            if (!a.startedAt) return 1;
            if (!b.startedAt) return -1;
            return new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime();
        });

    if (inProgress.length > 0) return inProgress[0];

    // Fallback: first not-completed course
    const notDone = allCourses.find((c) => c.enrollmentStatus !== UserEnrollmentStatus.Completed);
    if (notDone) return notDone;

    return allCourses[0];
};

export const roadmapHasCourse = (roadmap: IRoadmap, courseId: number): boolean =>
    roadmap.steps.some((step) => step.courses.some((course) => course.id === courseId));

// Roadmap that owns the course. A course can belong to several roadmaps —
// the selected one wins so we never switch away from it needlessly.
export const resolveRoadmapIdForCourse = (
    roadmaps: IRoadmap[],
    courseId: number,
    selectedRoadmapId: number
): number | null => {
    const selected = roadmaps.find((roadmap) => roadmap.id === selectedRoadmapId);
    if (selected && roadmapHasCourse(selected, courseId)) return selectedRoadmapId;

    return roadmaps.find((roadmap) => roadmapHasCourse(roadmap, courseId))?.id ?? null;
};

const resolveHeroVisible = (roadmap: IRoadmap | null): boolean => {
    if (!roadmap) return false;
    const allCourses = roadmap.steps.flatMap((s) => s.courses);
    return allCourses.some(
        (c) =>
            c.enrollmentStatus === UserEnrollmentStatus.Progress ||
            c.enrollmentStatus === UserEnrollmentStatus.Completed ||
            (c.enrollmentProgress ?? 0) > 0
    );
};

const contract = new ModelContract(
    {
        $roadmaps,
        $roadmapsLoading,
        $selectedRoadmapId,
        $selectedRoadmap,
        $activeRoadmapCourse,
        $isRoadmapHeroVisible,
        $isRoadmapSelectorOpen,
        $roadmapsConfig,
        $recommendedRoadmapId
    },
    {
        selectedRoadmapChanged,
        roadmapSelectorOpenChanged
    },
    {
        getRoadmapsFx,
        getRoadmapsConfigFx
    },
    {
        getRoadmapsFx,
        getRoadmapsConfigFx
    }
);

export type IRoadmapsContract = typeof contract;

export class RoadmapsModule extends ModuleDescription<IRoadmapsContract> {
    constructor(
        public userAuthModule: IUserAuthContract,
        public amplitudeFlagsModule: IAmplitudeFlagsContract,
        public metadataModule: IMetadataContract,
        public lessonsModule: ILessonsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        // Initial fetch waits for auth AND the flags identity to settle — the
        // roadmap step courses arrive remapped by the X-Course-Remap header,
        // which is only applied after the post-login flag fetch (see the same
        // gating in features/courses). $coursesMap.updates re-fires it on a
        // later remap change; `pending` dedupes same-cycle clocks.
        sample({
            clock: [
                this.userAuthModule.events.authenticated,
                this.amplitudeFlagsModule.stores.$identityFlagsSettled.updates,
                this.amplitudeFlagsModule.stores.$coursesMap.updates
            ],
            source: {
                student: this.userAuthModule.stores.$student,
                settled: this.amplitudeFlagsModule.stores.$identityFlagsSettled,
                pending: getRoadmapsFx.pending
            },
            filter: ({ student, settled, pending }) => Boolean(student) && settled && !pending,
            fn: () => undefined,
            target: getRoadmapsFx
        });

        sample({
            clock: this.lessonsModule.events.lessonCompletionTracked,
            target: getRoadmapsFx
        });

        sample({
            clock: this.userAuthModule.events.authenticated,
            target: getRoadmapsConfigFx
        });

        sample({
            clock: getRoadmapsConfigFx.doneData,
            target: $roadmapsConfig
        });

        sample({
            clock: getRoadmapsConfigFx.doneData,
            filter: (config) => Boolean(config?.onboardingId),
            target: setFunnelUserPropsFx
        });

        // Lazy experiment evaluation: only when the student's funnel has an
        // experiment-gated recommendation AND a flag fetch under the signed-in
        // identity has finished ($identityFlagsReady — not $flagsLoaded, which
        // can still be true from the pre-login fetch and would bucket by the
        // anonymous identity). client.variant() tracks the exposure, so this
        // must never fire for ineligible students. Re-fires after every flag
        // refetch, which only re-emits the same exposure/arm.
        sample({
            clock: [getRoadmapsConfigFx.doneData, this.amplitudeFlagsModule.stores.$identityFlagsReady],
            source: {
                config: $roadmapsConfig,
                flagsReady: this.amplitudeFlagsModule.stores.$identityFlagsReady
            },
            filter: ({ config, flagsReady }) => flagsReady && Boolean(config?.recommendation?.experimentKey),
            fn: ({ config }) => config!.recommendation!.experimentKey,
            target: this.amplitudeFlagsModule.events.roadmapOnboardingExperimentRequested
        });

        sample({
            source: {
                config: $roadmapsConfig,
                arm: this.amplitudeFlagsModule.stores.$roadmapOnboardingExperimentArm
            },
            fn: ({ config, arm }) => resolveRecommendedRoadmapKey(config, arm),
            target: $recommendedRoadmapKey
        });

        // Roadmap titles come from the API translated, so a language switch has to
        // refetch them — the store would otherwise keep the previous locale.
        sample({
            clock: contentLocaleChanged,
            filter: this.userAuthModule.stores.$student.map((student) => Boolean(student)),
            fn: () => undefined,
            target: getRoadmapsFx
        });

        sample({
            clock: getRoadmapsFx.doneData,
            target: $roadmaps
        });

        sample({
            source: {
                roadmaps: $roadmaps,
                metadata: this.metadataModule.stores.$metadata,
                recommendedId: $recommendedRoadmapId
            },
            filter: ({ roadmaps }) => roadmaps.length > 0,
            fn: resolveSelectedId,
            target: $selectedRoadmapId
        });

        sample({
            clock: selectedRoadmapChanged,
            target: $selectedRoadmapId
        });

        sample({
            clock: roadmapSelectorOpenChanged,
            target: $isRoadmapSelectorOpen
        });

        sample({
            clock: selectedRoadmapChanged,
            fn: (id) => ({ key: "selected_roadmap", value: String(id) }),
            target: this.metadataModule.events.setMetadataRequested
        });

        sample({
            source: $selectedRoadmap,
            fn: resolveActiveCourse,
            target: $activeRoadmapCourse
        });

        sample({
            source: $selectedRoadmap,
            fn: resolveHeroVisible,
            target: $isRoadmapHeroVisible
        });

        reset({
            clock: this.userAuthModule.events.logoutStudent,
            target: [
                $roadmaps,
                $selectedRoadmapId,
                $activeRoadmapCourse,
                $isRoadmapHeroVisible,
                $isRoadmapSelectorOpen,
                $roadmapsConfig,
                $recommendedRoadmapKey
            ]
        });

    }
}
